import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./TicketManager.css";

export default function TicketManager() {
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [showMyTickets, setShowMyTickets] = useState(false);

  const currentUsername = localStorage.getItem("username");
  const token = localStorage.getItem("token");

  const API_URL = process.env.REACT_APP_API_URL;

  let currentUserId = null;
  let role = "";

  if (token) {
    const decoded = jwtDecode(token);
    currentUserId = decoded.id;
    role = (decoded.role || "").toLowerCase();
  }

  const canReply =
    role === "admin" ||
    (role === "technician" &&
      selectedTicket?.assignedTo &&
      selectedTicket.assignedTo._id === currentUserId);

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    if (role !== "technician" && role !== "admin") {
      navigate("/user-tickets", { replace: true });
      return;
    }

    fetchTickets();
  }, [navigate, token, role]);

  const fetchTickets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ticket/getAll`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTickets(data);
    } catch (err) {
      console.error(err);
    }
  };

  const openTicket = async (id) => {
    try {
      const res = await fetch(
        `${API_URL}/api/ticket/details?id=${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      setSelectedTicket(data);
      setStatus(data.status);
    } catch (err) {
      console.error(err);
    }
  };

  const updateTicket = async () => {
    if (!selectedTicket) return;
    if (!message.trim() && status === selectedTicket.status) return;

    try {
      const res = await fetch(`${API_URL}/api/ticket/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: selectedTicket._id,
          ...(message.trim() && { message: message.trim() }),
          ...(status && { status }),
        }),
      });

      const updated = await res.json();

      if (!updated || !updated._id) {
        console.error("Invalid response:", updated);
        return;
      }

      setSelectedTicket(updated);
      setMessage("");

      setTickets((prev) =>
        prev.map((t) => (t._id === updated._id ? updated : t))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const assignToMe = async () => {
    if (!selectedTicket) return;

    try {
      const res = await fetch(`${API_URL}/api/ticket/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: selectedTicket._id }),
      });

      const updated = await res.json();

      if (!updated || !updated._id) {
        console.error("Invalid response:", updated);
        return;
      }

      setSelectedTicket(updated);
      setStatus(updated.status);

      setTickets((prev) =>
        prev.map((t) => (t._id === updated._id ? updated : t))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const displayedTickets = showMyTickets
    ? tickets.filter(
        (t) => t.assignedTo && t.assignedTo._id === currentUserId
      )
    : tickets;

  return (
    <div className="ticket-container">
      <div className="dashboard-wrapper">

      <h2 className="dashboard-title">🛠️ Ticket Manager Dashboard</h2>

      {role === "technician" && (
        <div className="filter-container">
          <label>
            <input
              type="checkbox"
              checked={showMyTickets}
              onChange={() => setShowMyTickets(!showMyTickets)}
            />{" "}
            Tickets assigned to me
          </label>
        </div>
      )}

      <table className="ticket-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Submitted By</th>
            <th>Location</th>
            <th>Status</th>
            <th>Assigned To</th>
            <th>Created</th>
          </tr>
        </thead>

        <tbody>
          {displayedTickets.map((ticket) => (
            <tr
              key={ticket._id}
              className="ticket-row"
              onClick={() => openTicket(ticket._id)}
            >
              <td>{ticket.title}</td>
              <td>{ticket.createdBy?.username}</td>
              <td>{ticket.location}</td>

              <td>
                <span
                  className={`status ${
                    ticket.status === "Open"
                      ? "status-open"
                      : ticket.status === "In Progress"
                      ? "status-progress"
                      : ticket.status === "Complete"
                      ? "status-complete"
                      : "status-waiting"
                  }`}
                >
                  {ticket.status}
                </span>
              </td>

              <td>
                {ticket.assignedTo
                  ? ticket.assignedTo.username
                  : "Unassigned"}
              </td>

              <td>{new Date(ticket.createdAt).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {selectedTicket && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{selectedTicket.title}</h3>

            <p>
              <strong>Description:</strong> {selectedTicket.description}
            </p>

            <p>
              <strong>Location:</strong> {selectedTicket.location}
            </p>

            <p>
              <strong>Created By:</strong> {selectedTicket.createdBy?.username}
            </p>

            <p>
              <strong>Assigned To:</strong>{" "}
              {selectedTicket.assignedTo
                ? selectedTicket.assignedTo.username
                : "Unassigned"}
            </p>

            {role === "technician" && !selectedTicket.assignedTo && (
              <button onClick={assignToMe}>Assign to Me</button>
            )}

            <div>
              <strong>Status:</strong>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="Open">Open</option>
                <option value="Waiting">Waiting</option>
                <option value="In Progress">In Progress</option>
                <option value="Complete">Complete</option>
              </select>

              <button onClick={updateTicket}>Update</button>
            </div>

            <hr />

            <div className="message-container">
              {selectedTicket.messages?.map((msg, i) => (
                <div
                  key={i}
                  className={`message ${
                    msg.authorName === currentUsername ? "user" : "other"
                  }`}
                >
                  <strong>{msg.authorName}:</strong>
                  <p>{msg.text}</p>
                </div>
              ))}
            </div>

            <textarea
              placeholder={
                canReply
                  ? "Type message..."
                  : "You must be assigned to this ticket to reply"
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={!canReply}
            />

            <button
              onClick={updateTicket}
              disabled={!canReply || !message.trim()}
            >
              Send
            </button>

            <button onClick={() => setSelectedTicket(null)}>
              Close
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
